'use client';

import { apiFetch } from '@/lib/utils/api';
import { LabeledSelect, SegmentedTabs, StatusBlock } from '@slims/ui/molecules';
import { buildPeriodTabs, periodMonth } from '@slims/ui/lib';
import { type MySlimsRulesForm, MySlimsView } from '@slims/ui/organisms';
import { PageHeader, SidebarLayout } from '@slims/ui/templates';
import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AiSuggestionsSidebar } from '@/components/myslims/ai-suggestions-sidebar';
import { useEnergyData } from '@/hooks/use-energy-data';
import { useDashboardSummary } from '@/hooks/use-dashboard-summary';
import { useMembers } from '@/hooks/use-members';
import { useEnergyMembers } from '@/hooks/use-energy-members';
import { useScopedConfig } from '@/hooks/use-scoped-config';
import { useMemberScopes } from '@/hooks/use-member-scopes';
import type { ScopeLeaf } from '@/hooks/use-scope-tree';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Le 6 schede periodo del mock v11 per MySlims: oggi/ieri (granularità
 * oraria) più il mese in corso e i tre precedenti (granularità giornaliera).
 * Diverso dal selettore a 4 schede di Monitoraggio/Incentivi.
 */
function usePeriodTabs() {
	return useMemo(() => {
		const today = new Date();
		return [
			{ value: 'oggi', label: 'Oggi' },
			{ value: 'ieri', label: 'Ieri' },
			...buildPeriodTabs(today),
		];
	}, []);
}

/**
 * MySlims è la vista personale del socio: a differenza di Dashboard/Monitoraggio
 * ecc. non affianca le configurazioni né ha una «Panoramica». Con più
 * configurazioni in ambito si lavora su **una sola alla volta**, scelta da un
 * selettore accanto al titolo.
 */
export default function MySlimsPage() {
	const { scopes, loading } = useMemberScopes();
	const role = useAuthStore((s) => s.role);
	if (loading) return <StatusBlock kind="loading" size="lg" message="Caricamento..." />;
	if (scopes.length === 0) {
		if (role !== 'member') return <Navigate to="/dashboard" replace />;
		return (
			<StatusBlock
				kind="empty"
				size="lg"
				message="Nessun profilo socio disponibile. Contatta l’amministratore della CER."
			/>
		);
	}
	return <MemberMySlimsPage scopes={scopes} />;
}

function MemberMySlimsPage({ scopes }: { scopes: ScopeLeaf[] }) {
	const isMulti = scopes.length > 1;
	const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
	const fullName = useAuthStore((s) => s.fullName);
	const firstName = fullName ? fullName.split(' ')[0] : null;

	const periodTabs = usePeriodTabs();
	// Il mock v11 parte sulla scheda "Oggi", non su "Mese in corso".
	const [period, setPeriod] = useState('oggi');

	// La configurazione scelta nel selettore, o la prima in ambito.
	const leaf = scopes.find((s) => s.orgId === selectedConfigId) ?? scopes[0];
	const orgId = leaf?.orgId;

	const { config } = useScopedConfig(orgId);
	const { totals } = useEnergyData(undefined, orgId);
	const condivisaMensile = totals.totalCondivisa > 0 ? Math.round(totals.totalCondivisa / 12) : 0;
	const tenantId = leaf?.tenantId ?? config.nome;

	if (!leaf) return null;

	return (
		<div>
			<PageHeader
				size="display"
				title={firstName ? `Ciao ${firstName}, buona giornata!` : 'MySlims'}
				actions={
					isMulti ? (
						<LabeledSelect label="Configurazione" value={leaf.orgId} onChange={setSelectedConfigId}>
							{scopes.map((s) => (
								<option key={s.orgId} value={s.orgId}>
									{s.cerNome} · {s.configNome}
								</option>
							))}
						</LabeledSelect>
					) : undefined
				}
			/>
			<SegmentedTabs
				items={periodTabs}
				value={period}
				onChange={setPeriod}
				ariaLabel="Periodo di riferimento dei KPI"
			/>
			<SidebarLayout
				main={<MySlimsPanel key={leaf.orgId} orgId={leaf.orgId} period={period} />}
				sidebar={
					leaf.features?.aiSuggestions !== 'disabled' ? (
						<AiSuggestionsSidebar
							cerNome={config.nome}
							tenantId={tenantId}
							condivisa={condivisaMensile}
							recCtx={{
								nome: config.nome,
								soci: config.soci,
								kwp: config.kwp,
							}}
						/>
					) : null
				}
			/>
		</div>
	);
}

function MySlimsPanel({ orgId, period }: { orgId: string; period: string }) {
	const role = useAuthStore((s) => s.role);
	const isAdmin = role === 'admin' || role === 'superadmin';
	const { config, loading: configLoading } = useScopedConfig(orgId);
	const fullMembers = useMembers(isAdmin ? orgId : null);
	const energyMembers = useEnergyMembers(isAdmin ? null : orgId);
	const { members, isLoading: membersLoading } = isAdmin ? fullMembers : energyMembers;
	const { sharedEnergy, isLoading: energyLoading } = useEnergyData(undefined, orgId);
	// I numeri del socio, non della configurazione (INB-363): la sua riga del
	// riepilogo del mese — consumi e produzione dai suoi contatori, quota di
	// energia condivisa. Chi amministra guarda la
	// configurazione, come prima.
	// Anche per chi amministra: la classifica usa i numeri veri di ogni socio (INB-356).
	const { summary, isLoading: summaryLoading } = useDashboardSummary(orgId, 'mese');
	const me = isAdmin ? undefined : members.find((m) => 'sonoIo' in m && m.sonoIo);
	// Le card personali seguono il periodo scelto (N-19): un riepilogo sulla sola
	// finestra, con `dayFrom = monthFrom` così consumi e quota condivisa sono
	// della stessa finestra.
	const personalRange = useMemo(() => personalWindow(period), [period]);
	const { summary: personalSummary, isLoading: personalLoading } = useDashboardSummary(
		me ? orgId : null,
		'mese',
		personalRange,
	);
	const myRow = me ? personalSummary?.members.find((r) => r.memberId === me.id) : undefined;
	const personal = me
		? {
				ruolo: me.ruolo,
				produzioneKwh: myRow?.productionTodayKwh ?? 0,
				consumoKwh: myRow?.consumptionTodayKwh ?? 0,
				condivisaKwh: myRow?.sharedMonthKwh ?? 0,
				stima: (() => {
					// Quota stimata dei tuoi valori nella finestra (INB-354); la condivisa
					// dipende anche dalla produzione della configurazione.
					const mine = personalSummary?.stima?.members[me.id];
					const produzioneConfig = personalSummary?.stima?.month.produzione ?? 0;
					return {
						consumo: mine?.consumo ?? 0,
						produzione: mine?.produzione ?? 0,
						condivisa: Math.max(mine?.consumo ?? 0, produzioneConfig),
					};
				})(),
			}
		: null;

	const cerSettings = (() => {
		try {
			return JSON.parse(config.settings ?? '{}') as Record<string, string>;
		} catch {
			return {} as Record<string, string>;
		}
	})();
	const rules: MySlimsRulesForm = {
		regoleGoverno: cerSettings.regoleGoverno ?? '',
		regoleAdesione: cerSettings.regoleAdesione ?? '',
		obiettiviCER: cerSettings.obiettiviCER ?? '',
	};

	async function saveRules(updated: MySlimsRulesForm) {
		const res = await apiFetch(`/api/cer?orgId=${orgId}`, {
			method: 'PATCH',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ settings: JSON.stringify({ ...cerSettings, ...updated }) }),
		});
		if (res.ok) {
			toast.success('Regole CER aggiornate');
		} else {
			toast.error('Errore salvataggio regole');
		}
	}

	return (
		<MySlimsView
			period={period}
			personal={personal}
			cerNome={config.nome}
			sharedEnergy={sharedEnergy}
			members={members}
			memberKpis={summary?.members ?? []}
			sociTarget={config.soci ?? 0}
			rules={rules}
			isAdmin={isAdmin}
			onSaveRules={saveRules}
			// Un solo flag per l'intera pagina: quasi ogni card legge dagli stessi
			// dati (Dashboard, Monitoraggio, Incentivi).
			loading={
				configLoading ||
				membersLoading ||
				energyLoading ||
				summaryLoading ||
				(!!me && personalLoading)
			}
		/>
	);
}

/**
 * La finestra delle card personali per il periodo di MySlims: oggi, ieri, il
 * mese in corso fino ad ora, o un mese passato intero (N-19).
 */
function personalWindow(period: string): { dayFrom: string; monthFrom: string; to: string } {
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	let from: Date;
	let to: Date = now;
	if (period === 'oggi') from = today;
	else if (period === 'ieri') {
		from = new Date(today.getTime() - 86_400_000);
		to = today;
	} else if (/^\d{4}-\d{2}$/.test(period)) {
		const { year, month } = periodMonth(period);
		from = new Date(year, month - 1, 1);
		const next = new Date(year, month, 1);
		to = next < now ? next : now;
	} else from = new Date(now.getFullYear(), now.getMonth(), 1);
	return { dayFrom: from.toISOString(), monthFrom: from.toISOString(), to: to.toISOString() };
}
